import type { ReactNode } from 'react'

import Image from 'next/image'

import { cn } from '@/lib/utils'
import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { routineSteps } from '@/constants/home'

/** Vertical offset per card, so the three steps cascade instead of sitting in a flat row. */
const STEP_OFFSET = ['', 'md:mt-16', 'md:mt-28']

export const Routine = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-routine-heading] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1,
            stagger: 0.045,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 76%' }
        })

        // Cards deal in one after another; the photo inside each unmasks upward.
        select('[data-routine-step]').forEach((card, index) => {
            const trigger = { trigger: card, start: 'top 85%' }

            gsap.from(card, {
                y: 56,
                opacity: 0,
                duration: 1,
                delay: index * 0.08,
                ease: 'nuvea',
                scrollTrigger: trigger
            })

            gsap.from(card.querySelector('[data-routine-media] img'), {
                scale: 1.22,
                duration: 1.6,
                delay: index * 0.08,
                ease: 'nuvea',
                scrollTrigger: trigger
            })
        })
    })

    return (
        <section ref={scope} id='routine' className='bg-shell py-24 md:py-32'>
            <div className='shell-x mx-auto w-full max-w-[1760px]'>
                <SplitWords
                    data-routine-heading=''
                    as='h2'
                    lines={['Three movements.', 'About thirty seconds.']}
                    className='type-h1 max-w-[18ch] text-ink'
                />

                <ol className='mt-14 grid grid-cols-1 items-start gap-8 md:mt-20 md:grid-cols-3 md:gap-6 lg:gap-10'>
                    {routineSteps.map((step, index) => (
                        <li
                            key={step.step}
                            data-routine-step=''
                            className={cn('flex flex-col', STEP_OFFSET[index])}
                        >
                            <div
                                data-routine-media=''
                                className='relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-cream'
                            >
                                <Image
                                    src={step.image}
                                    alt={step.alt}
                                    fill
                                    sizes='(min-width: 768px) 33vw, 100vw'
                                    className='object-cover will-change-transform'
                                />
                            </div>

                            <div className='mt-6 flex items-baseline justify-between gap-4 border-t border-stone-line pt-5'>
                                <h3 className='type-h3 text-ink'>
                                    {step.title}
                                </h3>

                                <span className='type-label text-slate-mute'>
                                    {step.step}
                                </span>
                            </div>

                            <p className='type-copy mt-4 max-w-[38ch] text-slate-soft'>
                                {step.body}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}
